import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/app-layout';
import { AppRoutes, ROUTES } from '@/routes';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { DemoSessionProvider } from '@/providers/DemoSessionProvider';
import { ProjectFormProvider } from '@/providers/ProjectFormProvider';
import { FieldConfigProvider } from '@/contexts/FieldConfigContext';
import { HeaderActionsProvider } from '@/contexts/HeaderActionsContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { DocumentApiProvider } from '@/providers/DocumentApiProvider';
import { getDocumentApiUrl } from '@/lib/data-service';
import { TemplateProvider } from '@/contexts/TemplateContext';
import { InvoiceTemplateProvider } from '@/contexts/InvoiceTemplateContext';
import { EmailTemplateProvider } from '@/contexts/EmailTemplateContext';
import { ProjectStatusProvider } from '@/contexts/ProjectStatusContext';
import { BootstrapGate } from '@/components/BootstrapGate';
import { PageViewTracker } from '@/components/PageViewTracker';
import { IntercomProvider } from '@/providers/IntercomProvider';
import { useAuth } from '@/providers/AuthProvider';
import type { IntercomUser } from '@/types/intercom';
// TEMPORARY - DEMO ONLY: Developer tools for error demonstration
import { DeveloperToolsDialog } from '@/components/developer-tools-dialog';
import { ErrorDisplay } from '@/components/error';
import { createAppError, type AppError } from '@/types/error.types';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompanyChange } from '@/hooks/use-company-change';
import { BulkActionsProvider } from '@/contexts/BulkActionsContext';
import { HelpDrawerProvider, HelpDrawer } from '@/components/HelpDrawer';
import { PermissionsProvider } from '@/contexts/PermissionsContext';
import { WorkspaceNavigationListener } from '@/components/WorkspaceNavigationListener';
import { DeepLinkHandler } from '@/components/DeepLinkHandler';
import { getWidgetDeepLinkTarget, hasPendingWidgetDeepLink, consumeStaleRedirectFlag, hasSessionMarker, setSessionMarker, isDeepLinkCompanySwitch } from '@/lib/deep-link-utils';
import { OnboardingProvider, useOnboarding } from '@/contexts/OnboardingContext';
import { WizardShell } from '@/components/onboarding/WizardShell';
import { TourProvider, useTourBackendSync } from '@/contexts/TourContext';
import { ProductTour } from '@/components/ProductTour';
import { useCompany } from '@/contexts/CompanyContext';
import { NoCompanyAccess } from '@/components/NoCompanyAccess';
import { InactiveRoleNotice } from '@/components/InactiveRoleNotice';
import { usePermissions } from '@/contexts/PermissionsContext';

// Helper to convert route path to page name for preferences
const routeToPageName = (pathname: string): string => {
  // Remove leading slash and get first segment
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'dashboard';
  
  // Handle project details route
  if (segments[0] === 'projects' && segments.length > 1) {
    return 'projects';
  }
  
  // Handle settings sub-routes
  if (segments[0] === 'settings' && segments.length > 1) {
    return 'settings';
  }

  // Handle reports sub-routes — map to their canonical page names
  if (segments[0] === 'reports' && segments.length > 1) {
    const reportsPageMap: Record<string, string> = {
      'wip': 'wip-reporting',
      'project-status': 'project-status',
      'budget-variance': 'budget-variance',
      'project-pl': 'project-pl',
      'project-forecast': 'project-forecast',
      'profitability': 'profitability-report',
    };
    return reportsPageMap[segments[1]] ?? segments[1];
  }
  
  return segments[0];
};

// Helper to convert page name to route path
const pageNameToRoute = (pageName: string): string => {
  const routeMap: Record<string, string> = {
    'dashboard': ROUTES.DASHBOARD,
    'projects': ROUTES.PROJECTS,
    'portfolio': ROUTES.PORTFOLIO,
    'utilisation': ROUTES.UTILISATION,
    'wip-reporting': ROUTES.WIP_REPORTING,
    'wip': ROUTES.WIP_REPORTING,
    'project-status': ROUTES.PROJECT_STATUS_REPORT,
    'budget-variance': ROUTES.BUDGET_VARIANCE_REPORT,
    'project-pl': ROUTES.PROJECT_PL_REPORT,
    'profitability': ROUTES.PROFITABILITY,

    'directory': ROUTES.DIRECTORY,
    'roles-rates': ROUTES.ROLES_RATES,
    'gantt': ROUTES.GANTT,
    'project-transactions': ROUTES.PROJECT_TRANSACTIONS,
    'audit-log': ROUTES.AUDIT_LOG,
    'settings': ROUTES.SETTINGS,
    'settings-general': ROUTES.SETTINGS_GENERAL,
    'settings-project': ROUTES.SETTINGS_PROJECT,
    'settings-user-preferences': ROUTES.SETTINGS_USER_PREFERENCES,
    'settings-integrations': ROUTES.SETTINGS_INTEGRATIONS,
    'settings-feature-labs': ROUTES.SETTINGS_FEATURE_LABS,
    'settings-user-permissions': ROUTES.SETTINGS_USER_PERMISSIONS,
    'settings-templates': ROUTES.SETTINGS_TEMPLATES,
    'settings-notifications': ROUTES.SETTINGS_NOTIFICATIONS,
    'project-templates': ROUTES.PROJECT_TEMPLATES,
    'user-preferences': ROUTES.USER_PREFERENCES,
    'project-creation-setup': ROUTES.PROJECT_CREATION_SETUP,
    'error-handling-demo': ROUTES.ERROR_HANDLING_DEMO,
    'column-configurator-demo': ROUTES.COLUMN_CONFIGURATOR_DEMO,
    'responsive-table-demo': ROUTES.RESPONSIVE_TABLE_DEMO,
  };
  
  return routeMap[pageName] || ROUTES.DASHBOARD;
};

function AppContent() {
  const { preferences, updatePreferences } = useUserPreferences();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDemoMode, isLiveMode } = useDataMode();
  const { authenticatedUser, isProduction } = useAuth();
  const { isWizardOpen, shouldAutoShow, hasCompleted, openWizard } = useOnboarding();
  const { isLoading: isCompanyLoading, availableCompanies, currentCompany, refreshCompanies } = useCompany();
  const { roleActive, isLoading: isPermissionsLoading, isMock: isPermissionsMock } = usePermissions();

  // Block app content when the user's assigned role has been made Inactive.
  // The navigator menus (AppLayout) still render, but every page is replaced
  // with a friendly notice. Never blocks in demo/tour mode or while the role
  // is still loading (isPermissionsMock covers both cases and API fallback).
  const isRoleInactive = !isPermissionsMock && !isPermissionsLoading && !roleActive;

  // Activate backend persistence for tour progress (auth is ready inside BootstrapGate)
  useTourBackendSync();

  // Auto-open the wizard once per session for admin users
  const hasAutoOpened = useRef(false);
  const isAdmin = isDemoMode || !isProduction || authenticatedUser?.applicationRole === 'Administrator';
  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (!authenticatedUser) return;
    if (!isAdmin) return;
    if (hasCompleted) return;
    if (!shouldAutoShow) return;
    hasAutoOpened.current = true;
    openWizard();
  }, [authenticatedUser, isAdmin, hasCompleted, shouldAutoShow, openWizard]);
  
  const hasNoCompany = !isCompanyLoading && isLiveMode && availableCompanies.length === 0 && !currentCompany;

  const [hasInitialized, setHasInitialized] = useState(false);

  // Force all pages to remount when the active company changes so every
  // hook that fetches company-scoped data on mount re-runs automatically.
  // When viewing a specific project, redirect to the project list first
  // because the old project ID won't exist in the new company.
  const [companyKey, setCompanyKey] = useState(0);
  useCompanyChange(() => {
    if (isDeepLinkCompanySwitch()) {
      // Deep-link navigation in progress — remount pages with the new
      // company context but don't redirect away from the target route.
      setCompanyKey(k => k + 1);
      return;
    }
    if (/^\/projects\/[^/]+/.test(location.pathname)) {
      navigate(ROUTES.PROJECTS, { replace: true });
      // Defer the key increment so the router processes the navigation
      // before AppRoutes remounts — prevents the old ProjectDetailsPage
      // from re-fetching the previous project ID against the new company.
      setTimeout(() => setCompanyKey(k => k + 1), 0);
    } else {
      setCompanyKey(k => k + 1);
    }
  });
  
  // TEMPORARY - DEMO ONLY: Developer tools dialog state
  const [showDevTools, setShowDevTools] = useState(false);
  // TEMPORARY - DEMO ONLY: Demo application error state (shown as overlay without crashing the app)
  const [demoAppError, setDemoAppError] = useState<AppError | null>(null);
  
  // Get current page from route
  const currentPage = routeToPageName(location.pathname);
  
  // Initialize and handle initial navigation
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);

      // Stale widget-param restores are flagged at module scope (before
      // React renders) by persistWidgetDeepLink / listenForEarlyNavigationRequest.
      if (consumeStaleRedirectFlag()) {
        setSessionMarker();
        navigate(pageNameToRoute(preferences.defaultLandingPage), { replace: true });
        return;
      }
      
      if (location.pathname === '/' || location.pathname === '') {
        // Root path — check for deep-link signals before navigating to landing page.
        const searchParams = new URLSearchParams(location.search);
        if (getWidgetDeepLinkTarget() || hasPendingWidgetDeepLink() || searchParams.get('projectId')) {
          setSessionMarker();
          return;
        }

        const getInitialPage = () => {
          if (preferences.rememberLastPage && preferences.lastVisitedPage) {
            return preferences.lastVisitedPage;
          }
          return preferences.defaultLandingPage;
        };
        
        const initialPage = getInitialPage();
        const initialRoute = pageNameToRoute(initialPage);
        setSessionMarker();
        navigate(initialRoute, { replace: true });
      } else {
        // Non-root path.  The workspace may have restored the iframe to
        // the last-visited URL from a previous browser session.  When
        // there is no session marker (new session) and no active
        // deep-link signal, redirect to the configured landing page —
        // unless the user opted into "remember last page".
        if (!hasSessionMarker() && !preferences.rememberLastPage) {
          const searchParams = new URLSearchParams(location.search);
          const hasDeepLinkSignal = getWidgetDeepLinkTarget() || hasPendingWidgetDeepLink() || searchParams.get('projectId');
          if (!hasDeepLinkSignal) {
            setSessionMarker();
            navigate(pageNameToRoute(preferences.defaultLandingPage), { replace: true });
            return;
          }
        }
        setSessionMarker();
      }
    }
  }, [hasInitialized, preferences, navigate, location.pathname, location.search]);

  // Redirect admin users to Settings when no company is set up yet
  useEffect(() => {
    if (!hasNoCompany || !isAdmin || !hasInitialized) return;
    if (location.pathname.startsWith('/settings')) return;
    navigate(ROUTES.SETTINGS_GENERAL, { replace: true });
  }, [hasNoCompany, isAdmin, hasInitialized, navigate, location.pathname]);
  
  // TEMPORARY - DEMO ONLY: Invisible keyboard shortcut for developer tools
  // Only enabled in development mode AND when using demo data
  useEffect(() => {
    const isDevelopment = import.meta.env.DEV;
    
    // Only enable in dev mode AND demo mode
    if (!isDevelopment || !isDemoMode) {
      return;
    }
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+D (or Cmd+Shift+D on Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setShowDevTools(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDemoMode]);
  
  // Update last visited page when route changes (but not on settings pages)
  useEffect(() => {
    if (hasInitialized && preferences.rememberLastPage) {
      const page = routeToPageName(location.pathname);
      if (page !== 'user-preferences' && page !== 'settings') {
        updatePreferences({ lastVisitedPage: page });
      }
    }
  }, [location.pathname, preferences.rememberLastPage, hasInitialized, updatePreferences]);

  // TEMPORARY - DEMO ONLY: Handler for triggering demo application errors
  const handleTriggerDemoError = (error: Error) => {
    const appError = createAppError(error);
    setDemoAppError(appError);
  };

  // TEMPORARY - DEMO ONLY: Handler for closing demo error (X button - returns to drawer)
  const handleCloseDemoError = () => {
    setDemoAppError(null);
    // Keep the drawer open - user returns to the drawer
  };

  // TEMPORARY - DEMO ONLY: Handler for retry/reload actions (closes both error and drawer)
  const handleDismissError = () => {
    setDemoAppError(null);
    setShowDevTools(false); // Close the drawer when using Try Again or Reload
  };

  // Note: projectContext was previously used for AppLayout header but is no longer needed
  // as the new AppLayout component handles project context differently via LeftMenu and breadcrumbs

  if (hasNoCompany && !isAdmin) {
    return <NoCompanyAccess onRetry={refreshCompanies} />;
  }

  return (
    <>
      <DeepLinkHandler />
      <PageViewTracker />
      {/* Onboarding wizard — rendered via portal so it floats above all app content */}
      {isWizardOpen && ReactDOM.createPortal(<WizardShell />, document.body)}
      {/* Product tour — portals to document.body, sits above all app content */}
      <ProductTour />
      {/* Global help drawer — rendered at root level, outside scroll containers */}
      <HelpDrawer />
      {/* TEMPORARY - DEMO ONLY: Developer tools dialog for error demonstration */}
      <DeveloperToolsDialog 
        open={showDevTools} 
        onOpenChange={setShowDevTools}
        onTriggerError={handleTriggerDemoError}
        hasActiveError={!!demoAppError}
      />
      {/* Main app content with conditional blur when dev tools is open (and no error) */}
      <div className={showDevTools && !demoAppError ? 'blur-sm pointer-events-none' : ''}>
        <AppLayout 
          currentPage={currentPage}
        >
          {isRoleInactive ? <InactiveRoleNotice /> : <AppRoutes key={companyKey} />}
        </AppLayout>
      </div>
      {/* TEMPORARY - DEMO ONLY: Demo application error overlay - rendered on top of everything */}
      {demoAppError && (
        <ErrorDisplay
          error={demoAppError}
          recovery={{
            canRetry: true,
            onRetry: handleDismissError, // Try Again closes both error and drawer
            canReload: true,
            onReload: handleDismissError, // Reload closes both error and drawer
          }}
          onClose={handleCloseDemoError} // X button only closes error, keeps drawer open
        />
      )}
    </>
  );
}

function useIntercomUser(): { intercomUser: IntercomUser | null } {
    const { authenticatedUser, instanceId } = useAuth();
    const email = authenticatedUser?.email;
    if (!email || !instanceId) {
        return { intercomUser: null };
    }
    return {
        intercomUser: { email, instanceId, name: authenticatedUser?.name },
    };
}

function IntercomWrapper({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
    const { intercomUser } = useIntercomUser();
    return <IntercomProvider user={intercomUser}>{children}</IntercomProvider>;
}

/**
 * OnboardingWrapper
 * Reads the authenticated user ID (available after BootstrapGate resolves)
 * and provides it to the OnboardingProvider so localStorage keys are user-scoped.
 */
function OnboardingWrapper({ children }: { children: React.ReactNode }) {
  const { authenticatedUser } = useAuth();
  return (
    <OnboardingProvider userId={authenticatedUser?.id ?? null}>
      {children}
    </OnboardingProvider>
  );
}

export default function App() {
  return (
    <TourProvider>
    <HelpDrawerProvider>
    <DemoSessionProvider>
      <HeaderActionsProvider>
        <IntercomWrapper>
          {/* Rendered above BootstrapGate so the postMessage listener is
              registered while the gate is still loading employee data.
              Without this, NavigationRequest messages from the workspace
              (e.g. "View in Access Projects" Feed links) are lost. */}
          <WorkspaceNavigationListener />
          <BootstrapGate>
            <OnboardingWrapper>
              <AppContent />
            </OnboardingWrapper>
          </BootstrapGate>
        </IntercomWrapper>
      </HeaderActionsProvider>
    </DemoSessionProvider>
    </HelpDrawerProvider>
    </TourProvider>
  );
}

// Wrapper component that provides context providers inside the router
export function AppWithProviders() {
  return (
    <CompanyProvider>
      <DocumentApiProvider documentApiUrl={getDocumentApiUrl()}>
        <PermissionsProvider>
        <ProjectStatusProvider>
          <TemplateProvider>
            <InvoiceTemplateProvider>
              <EmailTemplateProvider>
              <FieldConfigProvider>
                <ProjectFormProvider>
                  <BulkActionsProvider>
                    <App />
                  </BulkActionsProvider>
                </ProjectFormProvider>
              </FieldConfigProvider>
              </EmailTemplateProvider>
            </InvoiceTemplateProvider>
          </TemplateProvider>
        </ProjectStatusProvider>
        </PermissionsProvider>
      </DocumentApiProvider>
    </CompanyProvider>
  );
}
